// src/modules/hr/api.js — HR & Administration API client. All under /api/v1/hr/
import API from '../../services/api';

const base = '/hr';

// ── Dashboard ──
export const fetchHrDashboard = (params) => API.get(`${base}/dashboard/summary`, { params });

// ── Employees ──
export const fetchEmployees = (params) => API.get(`${base}/employees`, { params });
export const fetchEmployee = (id) => API.get(`${base}/employees/${id}`);
export const createEmployee = (data) => API.post(`${base}/employees`, data);
export const updateEmployee = (id, data) => API.patch(`${base}/employees/${id}`, data);
export const deleteEmployee = (id) => API.delete(`${base}/employees/${id}`);
export const fetchEmployeeCodeSettings = () => API.get(`${base}/employees/code-settings`);
export const updateEmployeeCodeSettings = (data) => API.patch(`${base}/employees/code-settings`, data);

// ── Assets ──
export const fetchAssets = (params) => API.get(`${base}/assets`, { params });
export const fetchAsset = (id) => API.get(`${base}/assets/${id}`);
export const createAsset = (data) => API.post(`${base}/assets`, data);
export const updateAsset = (id, data) => API.patch(`${base}/assets/${id}`, data);
export const assignAsset = (id, data) => API.post(`${base}/assets/${id}/assign`, data);
export const returnAsset = (id, data) => API.post(`${base}/assets/${id}/return`, data);
export const fetchAssetAssignments = (params) => API.get(`${base}/asset-assignments`, { params });

// ── Holidays ──
export const fetchHolidays = (params) => API.get(`${base}/holidays`, { params });
export const createHoliday = (data) => API.post(`${base}/holidays`, data);
export const updateHoliday = (id, data) => API.patch(`${base}/holidays/${id}`, data);
export const cancelHoliday = (id) => API.delete(`${base}/holidays/${id}`);
export const fetchClosurePreview = (id) => API.get(`${base}/holidays/${id}/closure-preview`);
export const applyHolidayClosure = (id) => API.post(`${base}/holidays/${id}/apply-closure`);

// ── Attendance ──
export const fetchAttendance = (params) => API.get(`${base}/attendance`, { params });
export const fetchMonthlySummary = (params) => API.get(`${base}/attendance/monthly-summary`, { params });
export const checkInAttendance = (data) => API.post(`${base}/attendance/check-in`, data);
export const checkOutAttendance = (data) => API.post(`${base}/attendance/check-out`, data);
export const markAttendance = (data) => API.post(`${base}/attendance/mark`, data);
export const importAttendance = (data) => API.post(`${base}/attendance/import`, data);
export const fetchLateArrivals = (params) => API.get(`${base}/attendance/late-arrivals`, { params });
export const actOnLateArrival = (id, data) => API.post(`${base}/attendance/late-arrivals/${id}/action`, data);

// Attendance Regularization (self-service)
export const applyRegularization = (data) => API.post(`${base}/attendance/regularizations`, data);
export const fetchRegularizations = (params) => API.get(`${base}/attendance/regularizations`, { params });
export const fetchRegularizationCalendar = (params) => API.get(`${base}/attendance/regularizations/calendar`, { params });
export const cancelRegularization = (id) => API.patch(`${base}/attendance/regularizations/${id}/cancel`);

// ── Leave ──
export const fetchLeaveTypes = () => API.get(`${base}/leave/types`);
export const fetchLeaveBalances = (params) => API.get(`${base}/leave/balances`, { params });
export const createLeaveBalance = (data) => API.post(`${base}/leave/balances`, data);
export const adjustLeaveBalance = (id, allocated) => API.patch(`${base}/leave/balances/${id}`, { allocated });
export const deleteLeaveBalance = (id) => API.delete(`${base}/leave/balances/${id}`);
export const fetchLeaves = (params) => API.get(`${base}/leave`, { params });
export const applyLeave = (data) => API.post(`${base}/leave`, data);
export const cancelLeave = (id) => API.patch(`${base}/leave/${id}/cancel`);
export const leaveManagerAction = (id, data) => API.patch(`${base}/leave/${id}/manager-action`, data);
export const leaveHrAction = (id, data) => API.patch(`${base}/leave/${id}/hr-action`, data);

// ── Documents ──
export const fetchDocuments = (params) => API.get(`${base}/documents`, { params });
export const createDocument = (data) => API.post(`${base}/documents`, data);
export const deleteDocument = (id) => API.delete(`${base}/documents/${id}`);

// ── Performance ──
export const fetchPerformance = (params) => API.get(`${base}/performance`, { params });
export const upsertPerformance = (data) => API.post(`${base}/performance`, data);
export const deletePerformance = (id) => API.delete(`${base}/performance/${id}`);

// ── Payroll (preparation) ──
export const fetchPayrollSummary = (params) => API.get(`${base}/payroll/summary`, { params });
export const payrollExportUrl = (params) => {
  const qs = new URLSearchParams(params).toString();
  return `${base}/payroll/export?${qs}`;
};

// ── Payroll runs, payslips & annual earnings (Phase 22.5) ──
export const generatePayrollRun = (data) => API.post(`${base}/payroll/runs`, data);
export const fetchPayrollRuns = (params) => API.get(`${base}/payroll/runs`, { params });
export const fetchPayrollRun = (id) => API.get(`${base}/payroll/runs/${id}`);
export const finalizePayrollRun = (id) => API.patch(`${base}/payroll/runs/${id}/finalize`);
export const markPayrollRunPaid = (id) => API.patch(`${base}/payroll/runs/${id}/mark-paid`);
export const cancelPayrollRun = (id) => API.patch(`${base}/payroll/runs/${id}/cancel`);
// Archive the run's payslip PDFs into each employee's folder. `force` re-uploads
// slips that are already published.
export const publishPayrollRun = (id, force = false) => API.post(`${base}/payroll/runs/${id}/publish`, { force });
export const publishPayslip = (id, force = false) => API.post(`${base}/payroll/payslips/${id}/publish`, { force });
export const fetchPayslips = (params) => API.get(`${base}/payroll/payslips`, { params });
export const fetchPayslip = (id) => API.get(`${base}/payroll/payslips/${id}`);
export const fetchMyPayslips = (params) => API.get(`${base}/payroll/my-payslips`, { params });
export const fetchAnnualEarnings = (params) => API.get(`${base}/payroll/annual`, { params });
export const payslipPdfUrl = (id) => `${base}/payroll/payslips/${id}/pdf`;

// ── Professional Tax slabs (global payroll config) ──
export const fetchPayrollCycle = () => API.get(`${base}/payroll/cycle-settings`);
export const updatePayrollCycle = (cycle) => API.patch(`${base}/payroll/cycle-settings`, { cycle });
export const previewPayrollCycle = (cycle, opts = {}) => API.post(`${base}/payroll/cycle-settings/preview`, { cycle, ...opts });
export const fetchPTaxSettings = () => API.get(`${base}/payroll/ptax-settings`);
export const updatePTaxSettings = (data) => API.patch(`${base}/payroll/ptax-settings`, data);

// ── Incentives (sales-based payroll adjustments) ──
export const fetchIncentives = (params) => API.get(`${base}/payroll/incentives`, { params });
export const saveIncentive = (data) => API.post(`${base}/payroll/incentives`, data);
export const importIncentives = (data) => API.post(`${base}/payroll/incentives/import`, data);
export const deleteIncentive = (id) => API.delete(`${base}/payroll/incentives/${id}`);

// ── Exit ──
export const fetchExits = (params) => API.get(`${base}/exits`, { params });
export const fetchExit = (id) => API.get(`${base}/exits/${id}`);
export const initiateExit = (data) => API.post(`${base}/exits`, data);
export const updateExitClearance = (id, data) => API.patch(`${base}/exits/${id}/clearance`, data);
export const completeExit = (id, data) => API.patch(`${base}/exits/${id}/complete`, data);
export const cancelExit = (id) => API.patch(`${base}/exits/${id}/cancel`);
